import React, { useEffect, useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Typography,
  Box,
  useTheme,
  TablePagination,
  IconButton,
  Tooltip,
  CircularProgress,
  Drawer,
  Divider,
  Button,
  alpha,
} from '@mui/material';
import { Eye, X, RotateCcw } from 'lucide-react';
import { usePayments } from '../../hooks/payment/usePayments';
import { format } from 'date-fns';
import type { Refund, RefundStatus } from '../../types/payment';

export const RefundsList: React.FC = () => {
  const theme = useTheme();
  const { refunds, refundsLoading, refreshRefunds, refundsTotalCount } = usePayments();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [selectedRefund, setSelectedRefund] = useState<Refund | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    refreshRefunds({ page: page + 1, pageSize });
  }, [page, pageSize, refreshRefunds]);

  const getStatusColor = (status: RefundStatus): 'success' | 'warning' | 'error' | 'default' => {
    switch (status) {
      case 'SUCCEEDED':
        return 'success';
      case 'PENDING':
        return 'warning';
      case 'FAILED':
      case 'CANCELLED':
        return 'error';
      default:
        return 'default';
    }
  };

  const formatReason = (reason: string) => {
    return reason
      .replace(/_/g, ' ')
      .toLowerCase()
      .replace(/\b\w/g, c => c.toUpperCase());
  };

  const formatCurrency = (cents: number, currency: string) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency,
    }).format(cents / 100);
  };

  const handleViewDetails = (refund: Refund) => {
    setSelectedRefund(refund);
    setDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setSelectedRefund(null);
  };

  if (refundsLoading && refunds.length === 0) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (!refundsLoading && refunds.length === 0) {
    return (
      <Box
        sx={{
          textAlign: 'center',
          py: 8,
          bgcolor: 'background.paper',
          borderRadius: 1,
          border: `1px dashed ${theme.palette.divider}`,
        }}
      >
        <Typography color="text.secondary">No refunds processed yet</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Refund History
      </Typography>

      <TableContainer
        component={Paper}
        elevation={0}
        sx={{ border: `1px solid ${theme.palette.divider}` }}
      >
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ color: 'text.secondary' }}>Date</TableCell>
              <TableCell sx={{ color: 'text.secondary' }}>Invoice</TableCell>
              <TableCell align="right" sx={{ color: 'text.secondary' }}>
                Amount
              </TableCell>
              <TableCell sx={{ color: 'text.secondary' }}>Reason</TableCell>
              <TableCell sx={{ color: 'text.secondary' }}>Status</TableCell>
              <TableCell align="right" sx={{ color: 'text.secondary' }}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {refunds.map(refund => (
              <TableRow key={refund.id} hover>
                <TableCell>{format(new Date(refund.createdAt), 'MMM d, yyyy')}</TableCell>
                <TableCell>Invoice #{refund.invoiceId}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>
                  {formatCurrency(refund.amountCents, refund.currency)}
                </TableCell>
                <TableCell>{formatReason(refund.reason)}</TableCell>
                <TableCell>
                  <Chip label={refund.status} color={getStatusColor(refund.status)} size="small" />
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="View Details">
                    <IconButton size="small" onClick={() => handleViewDetails(refund)}>
                      <Eye size={18} />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={refundsTotalCount}
        page={page}
        onPageChange={(_, newPage) => setPage(newPage)}
        rowsPerPage={pageSize}
        onRowsPerPageChange={e => setPageSize(parseInt(e.target.value, 10))}
        rowsPerPageOptions={[10, 25, 50]}
      />

      {/* Refund Detail Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={handleCloseDrawer}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 450 },
            p: 0,
          },
        }}
      >
        {selectedRefund && (
          <Box
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {/* Header */}
            <Box sx={{ p: 3, pb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box
                    sx={{
                      backgroundColor: alpha(theme.palette.primary.main, 0.1),
                      borderRadius: 2,
                      p: 1.25,
                      display: 'flex',
                    }}
                  >
                    <RotateCcw size={22} color={theme.palette.primary.main} />
                  </Box>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Refund Details
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}
                    >
                      Refund #{selectedRefund.id}
                    </Typography>
                  </Box>
                </Box>
                <IconButton onClick={handleCloseDrawer} size="small">
                  <X size={20} />
                </IconButton>
              </Box>
            </Box>

            <Divider />

            {/* Content */}
            <Box sx={{ flex: 1, overflowY: 'auto', p: 3 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {/* Status Badge */}
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  <Chip
                    label={selectedRefund.status}
                    color={getStatusColor(selectedRefund.status)}
                    sx={{ fontWeight: 600, px: 3, py: 2.5, fontSize: '0.9rem' }}
                  />
                </Box>

                {/* Amount Display */}
                <Box sx={{ textAlign: 'center', py: 2 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', mb: 0.5 }}
                  >
                    Refund Amount
                  </Typography>
                  <Typography variant="h3" color="error" sx={{ fontWeight: 700 }}>
                    {formatCurrency(selectedRefund.amountCents, selectedRefund.currency)}
                  </Typography>
                </Box>

                {/* Refund Info */}
                <Box
                  sx={{
                    backgroundColor: alpha(theme.palette.primary.main, 0.04),
                    borderRadius: 2,
                    p: 2.5,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Date
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {format(new Date(selectedRefund.createdAt), 'MMM d, yyyy h:mm a')}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Invoice
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      Invoice #{selectedRefund.invoiceId}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Reason
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {formatReason(selectedRefund.reason)}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mb: selectedRefund.stripeRefundId ? 2 : 0,
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Currency
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {selectedRefund.currency.toUpperCase()}
                    </Typography>
                  </Box>

                  {selectedRefund.stripeRefundId && (
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: selectedRefund.processedAt ? 2 : 0,
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        Stripe Refund ID
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 500, fontFamily: 'monospace', fontSize: '0.75rem' }}
                      >
                        {selectedRefund.stripeRefundId}
                      </Typography>
                    </Box>
                  )}

                  {selectedRefund.processedAt && (
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        Processed At
                      </Typography>
                      <Typography variant="body2" color="success.main" sx={{ fontWeight: 600 }}>
                        {format(new Date(selectedRefund.processedAt), 'MMM d, yyyy h:mm a')}
                      </Typography>
                    </Box>
                  )}
                </Box>

                {/* Failure Reason */}
                {selectedRefund.failureReason && (
                  <Box
                    sx={{
                      backgroundColor: alpha(theme.palette.error.main, 0.08),
                      borderRadius: 2,
                      p: 2.5,
                    }}
                  >
                    <Typography variant="subtitle2" color="error" sx={{ mb: 1 }}>
                      Failure Reason
                    </Typography>
                    <Typography variant="body2">{selectedRefund.failureReason}</Typography>
                  </Box>
                )}
              </Box>
            </Box>

            {/* Footer */}
            <Divider />
            <Box sx={{ p: 2.5 }}>
              <Button fullWidth onClick={handleCloseDrawer} sx={{ textTransform: 'none' }}>
                Close
              </Button>
            </Box>
          </Box>
        )}
      </Drawer>
    </Box>
  );
};
